<i18n>
{
  "fr": {
    "CustomerProjectCard": {
      "buttons": {
        "edit": "Modifier",
        "delete": "Supprimer",
        "addToCart": "Ajouter au panier"
      },
      "unitPrice": "soit {value} TTC / unité",
      "price": "{value} TTC",
      "priceWithoutVAT": "{value} HT",
      "confirmDeletion": "Êtes-vous sûr de vouloir supprimer ce projet ? Cette opération est définitive.",
      "addToCart": {
        "success": "Votre projet a bien été ajouté au {linkStart}panier{linkEnd}."
      },
      "outdated": "Le thème de votre projet a évolué. Pensez à le modifier avant de l'ajouter au panier.",
      "clickHere": "Cliquer ici"
    }
  }
}
</i18n>

<script>
import { requestToken } from '../api/api';
import axios from 'axios';
import Loader from '../mixins/Loader';

export default {
  name: 'CustomerProjectCard',
  mixins: [Loader],
  props: {
    cartUrl: {
      type: String,
      required: true,
    },
    imageUrl: {
      type: String,
      required: true,
    },
    size: {
      type: Object,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    quantities: {
      type: Array,
      required: true,
    },
    editionUrl: {
      type: String,
      required: true,
    },
    deletionUrl: {
      type: String,
      required: true,
    },
    addToCartUrl: {
      type: String,
      required: true,
    },
    isOutdated: Boolean,
  },
  data() {
    return {
      isLoading: false,
      selectedQuantity: this.quantities.find(({ isDefault }) => isDefault),
    };
  },
  methods: {
    confirmDeletion() {
      if (this.isLoading) {
        return false;
      }

      const isConfirmed = window.confirm(
        this.$t('CustomerProjectCard.confirmDeletion')
      );

      if (isConfirmed) {
        this.startLoading();

        const form = document.createElement('form');
        form.action = this.deletionUrl;
        form.method = 'post';

        const method = document.createElement('input');
        method.name = '_method';
        method.type = 'hidden';
        method.value = 'delete';

        const token = document.createElement('input');
        token.name = '_token';
        token.type = 'hidden';
        token.value = document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute('content');

        form.appendChild(method);
        form.appendChild(token);

        document.body.appendChild(form);

        form.submit();
      }
    },
    async addToCart() {
      if (this.isLoading || this.isOutdated) {
        return false;
      }

      this.startLoading();

      const token = await requestToken();
      const response = await axios.post(
        this.addToCartUrl,
        {
          variant_id: this.selectedQuantity.value,
        },
        {
          headers: {
            'X-CSRF-TOKEN': token,
            Accept: 'application/json',
          },
        }
      );
      const type = 200 === response.status ? 'success' : 'error';

      window.flashMessages = [
        {
          type: `alert-${type}`,
          message: this.$t(`CustomerProjectCard.addToCart.${type}`, {
            linkStart: `<a href="${this.cartUrl}">`,
            linkEnd: '</a>',
          }),
        },
      ];

      window.addToastMessages();

      this.stopLoading();
    },
    startLoading() {
      this.setLoader((this.isLoading = true));
    },
    stopLoading() {
      this.setLoader((this.isLoading = false));
    },
  },
};
</script>

<template>
  <div class="customer-project-card">
    <div v-show="isOutdated" class="customer-project-card__alert">
      <i class="i icon--warning"></i>
      <span>
        {{ $t('CustomerProjectCard.outdated') }}
        <a :href="editionUrl">
          {{ $t('CustomerProjectCard.clickHere') }}
        </a>
      </span>
      <i class="i icon--update"></i>
    </div>
    <div class="customer-project-card__body">
      <div class="customer-project-card__image-wrapper">
        <img
          :src="imageUrl"
          class="customer-project-card__image"
          alt="Image du gobelet personnalisé"
        />
      </div>

      <div class="customer-project-card__title-wrapper">
        <span class="customer-project-card__title">{{ label }}</span>
        <a :href="editionUrl" class="customer-project-card__edit">
          <i
            class="icon icon--cup-with-pencil customer-project-card__edit-icon"
          ></i>
          <span class="customer-project-card__edit-label">
            {{ $t('CustomerProjectCard.buttons.edit') }}
          </span>
        </a>
      </div>

      <div class="customer-project-card__footer">
        <div
          class="customer-project-card__actions customer-project-card__actions--project"
        >
          <div class="customer-project-card__prices">
            <span
              class="customer-project-card__price customer-project-card__price--total"
            >
              {{
                $t('CustomerProjectCard.price', {
                  value: selectedQuantity.price,
                })
              }}
            </span>
            <span
              class="customer-project-card__price customer-project-card__price--unit"
            >
              {{
                $t('CustomerProjectCard.priceWithoutVAT', {
                  value: selectedQuantity.priceWithoutVAT,
                })
              }}
            </span>
            <span
              class="customer-project-card__price customer-project-card__price--unit"
            >
              {{
                $t('CustomerProjectCard.unitPrice', {
                  value: selectedQuantity.unitPrice,
                })
              }}
            </span>
          </div>
        </div>
        <div
          class="customer-project-card__actions customer-project-card__actions--cart"
        >
          <div class="customer-project-card__action">
            <select
              v-model="selectedQuantity"
              class="customer-project-card__quantities-select"
            >
              <option
                v-for="quantity in quantities"
                :key="quantity.value"
                :value="quantity"
              >
                {{ quantity.label }}
              </option>
            </select>
            <button
              type="button"
              class="customer-project-card__delete"
              :disabled="isLoading"
              @click="confirmDeletion"
            >
              <span class="customer-project-card__delete-label">{{
                $t('CustomerProjectCard.buttons.delete')
              }}</span>
            </button>
          </div>
          <button
            type="button"
            class="customer-project-card__add-to-cart"
            :disabled="isLoading || isOutdated"
            @click="addToCart"
          >
            {{ $t('CustomerProjectCard.buttons.addToCart') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
